import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Pickaxe, Trophy, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const HashMiningSimulator = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [target, setTarget] = useState('0000');
  const [currentHash, setCurrentHash] = useState('');
  const [mining, setMining] = useState(false);
  const [score, setScore] = useState(0);
  const [blocksFound, setBlocksFound] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const generateRandomHash = () => {
    const chars = '0123456789abcdef';
    let hash = '';
    for (let i = 0; i < 64; i++) {
      hash += chars[Math.floor(Math.random() * chars.length)];
    }
    return hash;
  };

  const startMining = () => {
    setMining(true);
  };

  useEffect(() => {
    if (mining && !gameOver) {
      const interval = setInterval(() => {
        const hash = generateRandomHash();
        setCurrentHash(hash);
        setAttempts(prev => prev + 1);

        if (hash.startsWith(target)) {
          setMining(false);
          setBlocksFound(prev => prev + 1);
          const points = 500;
          setScore(prev => prev + points);
          toast.success(`Block mined! +${points} points`);
          
          if (blocksFound + 1 >= 3) {
            setTimeout(() => endGame(), 2000);
          }
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [mining, target, blocksFound, gameOver]);

  const endGame = async () => {
    setGameOver(true);
    setMining(false);
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'mining',
        score: score,
        time_taken: attempts
      });
      toast.success(`Mining complete! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Pickaxe className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Hash Mining Simulator</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Experience proof-of-work mining! Find hashes that start with '0000'. Each successful block earns 500 points. Mine 3 blocks to complete!
            </p>
            <Button
              onClick={() => setGameStarted(true)}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Mining
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Mining Complete!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Total Score</div>
              <div className="mt-4 text-zinc-400">
                Total Attempts: <span className="text-primary font-mono">{attempts}</span>
              </div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Mine Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Mining
        </Button>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-grotesk font-bold text-white">Hash Mining Simulator</h1>
          <div className="text-2xl font-mono font-bold text-primary" data-testid="game-score">{score}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 text-center">
            <Zap className="w-6 h-6 text-primary mx-auto mb-2" />
            <div className="text-3xl font-mono font-bold text-white" data-testid="attempts-count">{attempts}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Attempts</div>
          </div>
          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 text-center">
            <Pickaxe className="w-6 h-6 text-primary mx-auto mb-2" />
            <div className="text-3xl font-mono font-bold text-primary" data-testid="blocks-found">{blocksFound}/3</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Blocks Found</div>
          </div>
          <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 text-center">
            <Trophy className="w-6 h-6 text-primary mx-auto mb-2" />
            <div className="text-3xl font-mono font-bold text-white">{score}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wide">Score</div>
          </div>
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 mb-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Mining Target</h3>
          <div className="text-center mb-6">
            <div className="text-lg text-zinc-400 mb-2">Find a hash starting with:</div>
            <div className="text-3xl font-mono font-bold text-primary">{target}</div>
          </div>

          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Current Hash</h3>
          <div className="bg-black/50 rounded-lg p-4 mb-6 font-mono text-xs break-all" data-testid="current-hash">
            {currentHash || '0000000000000000000000000000000000000000000000000000000000000000'}
          </div>

          {!mining && blocksFound < 3 && (
            <Button
              onClick={startMining}
              data-testid="mine-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-4 w-full text-base"
            >
              <Pickaxe className="w-5 h-5 mr-2" />
              Start Mining
            </Button>
          )}

          {mining && (
            <div className="text-center">
              <div className="text-primary font-mono text-lg animate-pulse">Mining in progress...</div>
              <div className="text-zinc-500 text-sm mt-2">Computing hashes...</div>
            </div>
          )}
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">How It Works</h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Proof of Work mining involves repeatedly generating random hashes until finding one that meets the target difficulty. 
            In real Bitcoin, this process requires massive computational power. Here, we simulate this process at high speed!
          </p>
        </div>
      </div>
    </div>
  );
};

export default HashMiningSimulator;
